import type { ReactNode } from 'react';

import { Tooltip, TooltipContent, TooltipProvider, TooltipTrigger } from '../../ui/tooltip';
import { Absent } from '../measure/Absent';

export interface DimensionBarRow {
  readonly label: string;
  readonly value: number | null;
  readonly n: number | null;
}

const BAR_TONE: Record<'positive' | 'negative' | 'neutral', string> = {
  positive: 'bg-primary-ink',
  negative: 'bg-destructive',
  neutral: 'bg-muted-foreground',
};

export function DimensionBars(props: {
  title: string;
  domain: readonly [number, number];
  rows: readonly DimensionBarRow[];
  selectedLabel?: string;
}): ReactNode {
  const [min, max] = props.domain;
  const range = max - min;
  const zeroRatio = range > 0 ? Math.min(Math.max((0 - min) / range, 0), 1) : 0.5;

  return (
    <section aria-label={props.title} className="flex flex-col gap-2">
      <h3 className="text-title-sm">{props.title}</h3>
      <div role="list" className="flex flex-col gap-1">
        {props.rows.map((row) => {
          const isSelected = props.selectedLabel !== undefined && props.selectedLabel === row.label;

          if (row.value === null) {
            return (
              <div
                key={row.label}
                role="listitem"
                data-row={row.label}
                data-selected={isSelected ? 'true' : undefined}
                aria-current={isSelected ? 'true' : undefined}
                className={`flex items-center gap-3 rounded-md px-1.5 py-1 ${
                  isSelected ? 'bg-accent/40' : ''
                }`}
              >
                <div className="flex w-36 shrink-0 flex-col">
                  <span className="text-body-sm font-medium">{row.label}</span>
                  {row.n !== null && (
                    <span data-numeric className="text-caption text-muted-foreground">
                      {row.n}
                    </span>
                  )}
                </div>
                <div className="relative h-track flex-1 rounded-pill bg-secondary" />
                <div className="w-16 shrink-0 text-right">
                  <Absent reason="not_measured" />
                </div>
              </div>
            );
          }

          const isClamped = row.value < min || row.value > max;
          const clampedValue = Math.min(Math.max(row.value, min), max);
          const valRatio = range > 0 ? (clampedValue - min) / range : 0;

          let leftPercent: number;
          let widthPercent: number;
          let tone: 'positive' | 'negative' | 'neutral';

          if (clampedValue > 0) {
            leftPercent = zeroRatio * 100;
            widthPercent = Math.max(0, (valRatio - zeroRatio) * 100);
            tone = 'positive';
          } else if (clampedValue < 0) {
            leftPercent = valRatio * 100;
            widthPercent = Math.max(0, (zeroRatio - valRatio) * 100);
            tone = 'negative';
          } else {
            leftPercent = zeroRatio * 100;
            widthPercent = 0;
            tone = 'neutral';
          }

          const formattedValue = row.value > 0 ? `+${row.value}` : `${row.value}`;

          return (
            <div
              key={row.label}
              role="listitem"
              data-row={row.label}
              data-selected={isSelected ? 'true' : undefined}
              data-clamped={isClamped ? 'true' : undefined}
              aria-current={isSelected ? 'true' : undefined}
              className={`flex items-center gap-3 rounded-md px-1.5 py-1 ${
                isSelected ? 'bg-accent/40' : ''
              }`}
            >
              <div className="flex w-36 shrink-0 flex-col">
                <span className="text-body-sm font-medium">{row.label}</span>
                {row.n !== null && (
                  <span data-numeric className="text-caption text-muted-foreground">
                    {row.n}
                  </span>
                )}
              </div>
              <div className="relative flex h-track flex-1 items-center rounded-pill bg-secondary">
                <div
                  className="absolute inset-y-0 w-px bg-border"
                  style={{ left: `${zeroRatio * 100}%` }}
                />
                <div
                  role="img"
                  aria-label={`${row.label}: ${formattedValue}`}
                  data-slot="bar"
                  className={`absolute h-track rounded-pill ${BAR_TONE[tone]}`}
                  style={{ left: `${leftPercent}%`, width: `${widthPercent}%` }}
                />
              </div>
              <div className="w-16 shrink-0 text-right">
                <span
                  data-numeric
                  data-clamped={isClamped ? 'true' : undefined}
                  className="text-body-sm"
                >
                  {formattedValue}
                </span>
              </div>
            </div>
          );
        })}
      </div>
    </section>
  );
}

const MINUS = '−';

function signedValue(value: number): string {
  return value > 0 ? `+${value}` : `${value}`.replace('-', MINUS);
}

/** Where a bar starts and how long it runs along the value axis, as
 *  percentages of the domain, clamped to it. */
function extent(value: number, domain: readonly [number, number]) {
  const [min, max] = domain;
  const range = max - min;
  const ratio = (v: number) => (range > 0 ? (Math.min(Math.max(v, min), max) - min) / range : 0.5);
  const zero = ratio(0);
  const end = ratio(value);
  return { zero: zero * 100, start: Math.min(zero, end) * 100, length: Math.abs(end - zero) * 100 };
}

function toneOf(value: number): string {
  return value > 0 ? BAR_TONE.positive : value < 0 ? BAR_TONE.negative : BAR_TONE.neutral;
}

type MeasuredRow = DimensionBarRow & { readonly value: number };

/**
 * One dimension's balances as a chart on a fixed axis: columns for a few
 * categories, horizontal bars for a long list. Every bar is focusable and
 * opens a tooltip naming its label and whatever `describe` adds, on hover and
 * on keyboard focus alike; the value is also printed beside the bar.
 */
export function DimensionChart(props: {
  title: string;
  domain: readonly [number, number];
  rows: readonly DimensionBarRow[];
  orientation: 'columns' | 'bars';
  /** Names the axis, e.g. "Saldo de −100 a 100". */
  axisLabel: string;
  describe: (row: MeasuredRow) => string;
}): ReactNode {
  const [min, max] = props.domain;
  const mid = (max + min) / 2;
  const quarter = (max - min) / 4;
  const ticks = [max, mid + quarter, mid, mid - quarter, min];
  const zero = extent(0, props.domain).zero;
  const axis = (
    <ul
      aria-label={props.axisLabel}
      className={
        props.orientation === 'columns'
          ? 'text-caption text-muted-foreground flex h-40 w-8 shrink-0 flex-col justify-between text-right'
          : 'text-caption text-muted-foreground flex flex-row-reverse justify-between'
      }
    >
      {ticks.map((tick) => (
        <li key={tick} data-numeric>
          {`${tick}`.replace('-', MINUS)}
        </li>
      ))}
    </ul>
  );

  const bar = (row: DimensionBarRow): ReactNode => {
    if (row.value === null) return <Absent reason="not_measured" />;
    const measured = row as MeasuredRow;
    const { start, length } = extent(measured.value, props.domain);
    const columns = props.orientation === 'columns';
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            type="button"
            aria-label={`${row.label}: ${signedValue(measured.value)}`}
            data-slot="bar"
            className={`focus-visible:ring-ring absolute rounded-sm outline-none focus-visible:ring-2 ${toneOf(measured.value)} ${
              columns ? 'inset-x-2' : 'inset-y-0'
            }`}
            style={
              columns
                ? { bottom: `${start}%`, height: `${length}%` }
                : { left: `${start}%`, width: `${length}%` }
            }
          />
        </TooltipTrigger>
        <TooltipContent>
          <p className="font-medium">{row.label}</p>
          <p>{props.describe(measured)}</p>
        </TooltipContent>
      </Tooltip>
    );
  };

  const valueLabel = (row: DimensionBarRow): ReactNode =>
    row.value === null ? null : (
      <span data-numeric className="text-caption">
        {signedValue(row.value)}
      </span>
    );

  return (
    <TooltipProvider>
      <section aria-label={props.title} className="flex flex-col gap-3">
        <h3 className="text-title-sm">{props.title}</h3>
        {props.orientation === 'columns' ? (
          <div className="flex gap-2">
            {axis}
            <div role="list" className="flex min-w-0 flex-1 flex-col gap-1">
              <div className="relative flex h-40">
                <div className="bg-border absolute inset-x-0 h-px" style={{ bottom: `${zero}%` }} />
                {props.rows.map((row) => {
                  const span = row.value === null ? null : extent(row.value, props.domain);
                  return (
                    <div
                      key={row.label}
                      role="listitem"
                      data-row={row.label}
                      className="relative flex-1"
                    >
                      {bar(row)}
                      {span !== null && row.value !== null && (
                        <div
                          className="absolute inset-x-0 text-center"
                          style={
                            row.value >= 0
                              ? { bottom: `${span.start + span.length}%` }
                              : { top: `${100 - span.start}%` }
                          }
                        >
                          {valueLabel(row)}
                        </div>
                      )}
                    </div>
                  );
                })}
              </div>
              <div aria-hidden className="text-caption text-muted-foreground flex">
                {props.rows.map((row) => (
                  <span key={row.label} className="flex-1 truncate text-center">
                    {row.label}
                  </span>
                ))}
              </div>
            </div>
          </div>
        ) : (
          <div className="flex flex-col gap-2">
            <div role="list" className="flex flex-col gap-2">
              {props.rows.map((row) => (
                <div
                  key={row.label}
                  role="listitem"
                  data-row={row.label}
                  className="flex items-center gap-2"
                >
                  <span className="text-caption text-muted-foreground w-28 shrink-0 truncate text-right">
                    {row.label}
                  </span>
                  <div className="relative h-4 min-w-0 flex-1">
                    <div
                      className="bg-border absolute inset-y-0 w-px"
                      style={{ left: `${zero}%` }}
                    />
                    {bar(row)}
                  </div>
                  <span className="w-10 shrink-0 text-right">{valueLabel(row)}</span>
                </div>
              ))}
            </div>
            <div className="flex gap-2">
              <span className="w-28 shrink-0" />
              <div className="min-w-0 flex-1">{axis}</div>
              <span className="w-10 shrink-0" />
            </div>
          </div>
        )}
      </section>
    </TooltipProvider>
  );
}
